import querystring from 'querystring'

import classNames from 'classnames'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'

import Icon from '@/components/Icon/Icon'
import Typeahead from '@/components/Typeahead/Typeahead'
import filtersService from '@/services/filters.service'
import { useAppSelector } from '@/store/hooks'
import { CountFilterRowType, NameFilterRowType } from '@/types/filters'
import { CommandersPageProps } from '@/types/page'
import useDirectories from '@/utils/useDirectories'

import styles from './FilterPanel.module.scss'
import FilterPanelButtons from './FilterPanelButtons'

type Props = {
  data: CommandersPageProps['data']
}

const FilterPanel = ({ data }: Props) => {
  const { countFilters, countForm, error, nameFilters, nameForm } = useAppSelector((state) => state.filters)
  const { dir } = useDirectories()
  const router = useRouter()

  useEffect(() => {
    filtersService.initializeForm(router.asPath)
  }, [])

  const handleSubmit = () => filtersService.submit({ card: data.container.json_dict.card, dir, router })

  const hasQueryParams = querystring.parse(router.asPath.split('?')[1]).f !== undefined

  return (
    <Card className='flex-grow-1 rounded-0 shadow-sm w-100'>
      <div className='d-flex justify-content-between m-1 mb-0'>
        <OverlayTrigger
          overlay={(props) => (
            <Tooltip id='button-tooltip' {...props}>
              <b>Deck Filters</b> allow you to filter the underlying dataset
            </Tooltip>
          )}
          placement='right'
        >
          <div className={styles.title}>Deck Filters</div>
        </OverlayTrigger>
        {(countFilters.length > 0 || nameFilters.length > 0 || hasQueryParams) && (
          <Button
            className={styles.resetButton}
            onClick={() => {
              filtersService.reset()
              handleSubmit()
            }}
            variant='danger'
          >
            <Icon icon='times' size='sm' />
          </Button>
        )}
      </div>
      <Card.Body className='p-2'>
        <div className={styles.form}>
          <div className={styles.row}>
            <select
              className={classNames(styles.filtersType, 'form-control')}
              onChange={(e) =>
                filtersService.setForm({
                  filter: {
                    preposition: e.target.value as NameFilterRowType['preposition'],
                  },
                  type: 'name',
                })
              }
              value={nameForm.preposition}
            >
              <option value='in'>With</option>
              <option value='out'>Without</option>
            </select>
            <Typeahead
              className={styles.filtersName}
              clearButton
              icon={undefined}
              onChange={(value) =>
                filtersService.setForm({ filter: { name: value as NameFilterRowType['name'] }, type: 'name' })
              }
              placeholder='Card name'
              selected={nameForm.name}
            />
            <button
              className='btn btn-primary'
              disabled={nameForm.name.length === 0}
              onClick={() => filtersService.addFilter({ filter: nameForm, type: 'name' })}
            >
              +
            </button>
          </div>
          <div className={styles.row}>
            <select
              className={classNames('form-control', styles.filtersType)}
              onChange={(e) =>
                filtersService.setForm({
                  filter: {
                    type: e.target.value as any,
                  },
                  type: 'count',
                })
              }
              value={countForm.type}
            >
              <option value='c'>Creatures</option>
              <option value='i'>Instants</option>
              <option value='s'>Sorceries</option>
              <option value='a'>Artifacts</option>
              <option value='e'>Enchantments</option>
              <option value='b'>Battles</option>
              <option value='p'>Planeswalkers</option>
              <option value='l'>Lands</option>
              <option value='d'>Price</option>
              <option value='n'>Salt</option>
              <option value='g'>Game changers</option>
              {/* <option value='['>Bracket (*)</option> */}
            </select>
            <select
              className={classNames(styles.filtersOp, 'form-control')}
              onChange={(e) =>
                filtersService.setForm({
                  filter: {
                    op: e.target.value as CountFilterRowType['op'],
                  },
                  type: 'count',
                })
              }
              value={countForm.op}
            >
              <option value='gt'>≥</option>
              <option value='lt'>≤</option>
              <option value='eq'>=</option>
              <option value='ne'>≠</option>
            </select>
            <input
              className={classNames(styles.filtersNum, 'form-control')}
              onChange={(e) =>
                filtersService.setForm({
                  filter: {
                    number: parseFloat(e.target.value) as CountFilterRowType['number'],
                  },
                  type: 'count',
                })
              }
              placeholder='Number'
              type='number'
              value={countForm.number}
            />
            <button
              className='btn btn-primary'
              disabled={`${countForm.number}`.length === 0}
              onClick={() => filtersService.addFilter({ filter: countForm, type: 'count' })}
            >
              +
            </button>
          </div>
          <div className={styles.filterAlerts}>
            {nameFilters.map((filter) => (
              <Alert
                className={styles.filterAlert}
                key={filter.key}
                variant={filter.preposition === 'in' ? 'success' : 'danger'}
              >
                <span>{`${{ in: 'With', out: 'Without' }[filter.preposition || '']} ${filter.name}`}</span>
                <Icon
                  className={styles.delIcon}
                  icon='times'
                  onClick={() =>
                    filtersService.removeFilter({
                      key: filter.key,
                      type: 'name',
                    })
                  }
                />
              </Alert>
            ))}
            {countFilters.map((filter) => (
              <Alert className={styles.filterAlert} key={filter.key} variant='info'>
                <span>
                  {`${
                    {
                      '[': `bracket`,
                      a: `artifacts`,
                      b: `battles`,
                      c: `creatures`,
                      d: `price`,
                      e: `enchantments`,
                      g: `game changers`,
                      i: `instants`,
                      l: `lands`,
                      n: `salt`,
                      p: `planeswalkers`,
                      s: `sorceies`,
                    }[filter.type as CountFilterRowType['type']]
                  }`}{' '}
                  {`${
                    {
                      eq: '=',
                      gt: '≥',
                      lt: '≤',
                      ne: '≠',
                    }[filter.op || '']
                  }`}{' '}
                  {filter.number}
                </span>
                <Icon
                  className={styles.delIcon}
                  icon='times'
                  onClick={() =>
                    filtersService.removeFilter({
                      key: filter.key,
                      type: 'count',
                    })
                  }
                />
              </Alert>
            ))}
            <FilterPanelButtons
              disabled={countFilters.length === 0 && nameFilters.length === 0 && !hasQueryParams}
              onClick={handleSubmit}
            />
            {error && (
              <Alert className={styles.filterAlert} variant='danger'>
                {error}
              </Alert>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}

export default FilterPanel
